import { useEffect } from "react";
import { Divider, Loading, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetManualPaymentDetailsMutation } from "@/redux/apis/payInvoice/payInvoiceApi";
import { useAppSelector } from "@/redux/hooks";
import { payByCreditCardSelector } from "@/redux/slices/payInvoice/selectors";
import { PaymentMethodDetails } from "@/redux/slices/payInvoice/types";
import { addToast, clsx, formatCurrency } from "@/utils";
import { usePayInvoicePageContext } from "../context";

interface ManualPaymentDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
  paymentMethodDetails: PaymentMethodDetails;
}

export const ManualPaymentDetails = ({ paymentMethodDetails, className, ...props }: ManualPaymentDetailsProps) => {
  const { params, payInvoice } = usePayInvoicePageContext();
  const { currency } = payInvoice;
  const [getManualPaymentDetails, { isLoading }] = useGetManualPaymentDetailsMutation();
  const { manualPaymentDetails } = useAppSelector(payByCreditCardSelector);

  useEffect(() => {
    getManualPaymentDetails({ ...params, paymentMethodId: paymentMethodDetails.id })
      .unwrap()
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  }, [params, paymentMethodDetails, getManualPaymentDetails]);

  return (
    <div className={clsx("relative", className)} {...props}>
      {isLoading && <Loading />}
      <div className="mb-[18px] flex flex-row items-center text-neutral-dark-gray">
        <Typography variant="action" className="flex-1">
          Item
        </Typography>
        <Typography variant="action" className="flex">
          Amount
        </Typography>
      </div>
      <div className="mb-4 flex flex-col">
        <div className="flex flex-row">
          <Typography variant="paragraph" className="flex">
            Total before GST:
          </Typography>
          <div className="mb-[4px] flex-1 border-b-[1px] border-dashed border-neutral-dark-gray" />
          {manualPaymentDetails && (
            <Typography variant="paragraph" className="flex">{`${currency} ${formatCurrency(
              manualPaymentDetails.amounts.subTotal,
              2,
              currency
            )}`}</Typography>
          )}
        </div>
        <div className="flex flex-row">
          <Typography variant="paragraph" className="flex">
            Credit Card Fee:
          </Typography>
          <div className="mb-[4px] flex-1 border-b-[1px] border-dashed border-neutral-dark-gray" />
          {manualPaymentDetails && (
            <Typography variant="paragraph" className="flex">{`${currency} ${formatCurrency(
              manualPaymentDetails.amounts.fees,
              2,
              currency
            )}`}</Typography>
          )}
        </div>
      </div>
      <Divider className="mb-5" />
      <div className="mb-[18px] flex flex-row items-center">
        <Typography variant="paragraph" className="flex-1">
          Total Amount
        </Typography>
        {manualPaymentDetails && (
          <Typography variant="h2" className="flex">{`${currency} ${formatCurrency(
            manualPaymentDetails.amounts.total,
            2,
            currency
          )}`}</Typography>
        )}
      </div>
    </div>
  );
};
